/* eslint-disable arrow-body-style */

import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { Input, InputProps } from 'tea-component';
import ListRenderGuide from '@gp/components/ListRenderGuide';
import GeneralDeletableTag from '@gp/components/GeneralDeletableTag';
import { convert2Tags, getTagInputValue } from './utils';

import type { IBaseProps } from '@home/types/common';
import type { IDynamicFormControlValueAccessor } from '@gp/components/DynamicForm/components/FormControl/types';
import type { ITagInputValue } from './types';

import './index.scss';

interface ITagInputProps extends IBaseProps, IDynamicFormControlValueAccessor<ITagInputValue> {
  splitter?: string;
  getTagText?: (tag: string) => string;
  inputProps?: Omit<InputProps, 'placeholder' | 'value' | 'onChange' | 'onBlur' | 'onFocus'>;
  validateTag?: (tag: string) => boolean;
}

const TagInput: React.FC<ITagInputProps> = (props) => {
  const {
    style,
    className,
    placeholder,
    splitter = ';',
    getTagText,
    inputProps,
    validateTag,
    value: valueProps,
    onChange,
  } = props;
  const { className: inputClassName } = inputProps || {};

  const [value, setValue] = useState<ITagInputValue>(() => getTagInputValue(valueProps));

  const handleInputChange = (payload: string) => {
    const valueTmp = { ...value, input: payload };
    setValue(valueTmp);
    onChange?.(valueTmp);
  };

  const handleAddTag = (payload: string) => {
    if (!payload) return;
    const { tags } = value || {};
    const tagsComing = convert2Tags(payload, { tags, splitter, getTagText, validateTag });
    const tagsTmp = [...tags, ...tagsComing];
    setValue({ tags: tagsTmp, input: '' });
    onChange?.({ tags: tagsTmp, input: '' });
  };

  useEffect(() => {
    setValue(getTagInputValue(valueProps));
  }, [valueProps]);

  return (
    <div style={style} className={classnames('dbg-common-tag-input', className)}>
      <ListRenderGuide list={value?.tags}>
        <ul className="dbg-common-tag-input__tag-list">
          {value?.tags?.map((tag) => {
            const { text: tagText, value: tagValue, valid: tagValid } = tag || {};
            return (
              <li key={tagValue} className="dbg-common-tag-input__tag-item">
                <GeneralDeletableTag
                  isError={!tagValid}
                  text={tagValue}
                  formatter={() => tagText}
                  onDelete={(tagValue) => {
                    const tagsTmp = value?.tags?.filter((tag) => {
                      return tag?.value !== tagValue;
                    });
                    const valueTmp = { ...value, tags: tagsTmp || [] };
                    setValue(valueTmp);
                    onChange?.(valueTmp);
                  }}
                />
              </li>
            );
          })}
        </ul>
      </ListRenderGuide>
      <Input
        {...inputProps}
        className={classnames('dbg-common-tag-input__input', inputClassName)}
        placeholder={value?.tags?.length > 0 || !!value?.input ? '' : placeholder}
        value={value?.input}
        onChange={(payload) => {
          const valueTmp = { ...value, input: payload };
          setValue(valueTmp);
          handleInputChange(payload);
        }}
        onPressEnter={(payload, e) => {
          e?.stopPropagation();
          handleAddTag(payload);
        }}
        onBlur={(e) => {
          e?.stopPropagation();
          handleAddTag(value?.input);
        }}
        onPaste={(e) => {
          handleAddTag(e?.clipboardData?.getData('text'));
        }}
      />
    </div>
  );
};

export default TagInput;
